
import React from 'react';
import { CircuitMode, ComponentState } from '../types';

interface Props {
  mode: CircuitMode;
  state: ComponentState;
}

const TheoryPanel: React.FC<Props> = ({ mode, state }) => {
  const { voltage, resistance1, resistance2, isClosed } = state;
  
  const r_total = mode === CircuitMode.SERIES 
    ? (resistance1 + resistance2) 
    : (1 / (1/resistance1 + 1/resistance2));
  
  const i_total = isClosed ? (voltage / r_total) : 0;
  
  // Detail calculations
  const v1 = mode === CircuitMode.SERIES ? (i_total * resistance1) : voltage;
  const v2 = mode === CircuitMode.SERIES ? (i_total * resistance2) : voltage;
  const i1 = mode === CircuitMode.SERIES ? i_total : (voltage / resistance1);
  const i2 = mode === CircuitMode.SERIES ? i_total : (voltage / resistance2);

  return (
    <div className="max-w-4xl mx-auto grid grid-cols-1 md:grid-cols-2 gap-8">
      <div>
        <h3 className="text-xl font-bold mb-3 text-slate-800">
          {mode === CircuitMode.SERIES ? '串联电路特点' : '并联电路特点'}
        </h3>
        <p className="text-slate-600 mb-4 leading-relaxed">
          {mode === CircuitMode.SERIES 
            ? '在串联电路中，各用电器首尾相连。电流只有一条路径，各处电流相等，总电压等于各部分电压之和。'
            : '在并联电路中，用电器并列连接在电路两点间。电流有多条路径，干路电流等于各支路电流之和，各支路电压相等。'}
        </p>
        <div className="space-y-2 font-mono text-sm">
          <div className="bg-blue-50 p-2 rounded flex justify-between">
            <span>总电阻 (R_total):</span>
            <span className="font-bold">{mode === CircuitMode.SERIES ? 'R1 + R2' : '1/(1/R1 + 1/R2)'} = {r_total.toFixed(2)} Ω</span>
          </div>
          <div className="bg-blue-50 p-2 rounded flex justify-between">
            <span>总电流 (I_total):</span>
            <span className="font-bold">U / R_total = {i_total.toFixed(2)} A</span>
          </div>
        </div>
      </div>
      
      <div className="bg-white p-4 rounded-xl shadow-inner border border-slate-100">
        <h4 className="font-bold text-slate-700 mb-4 border-b pb-2">实时参数监测 (Ohm's Law)</h4>
        <div className="space-y-4">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 bg-amber-100 rounded-full flex items-center justify-center text-amber-700 font-bold">R1</div>
            <div className="flex-1">
              <div className="flex justify-between text-xs text-slate-500 mb-1">
                <span>分电压: {isClosed ? v1.toFixed(2) : 0}V</span>
                <span>分电流: {isClosed ? i1.toFixed(2) : 0}A</span>
              </div>
              <div className="w-full bg-slate-100 h-1.5 rounded-full overflow-hidden">
                <div className="bg-amber-400 h-full" style={{ width: `${isClosed ? (v1/voltage)*100 : 0}%` }}></div>
              </div>
            </div>
          </div>

          <div className="flex items-center gap-4">
            <div className="w-12 h-12 bg-emerald-100 rounded-full flex items-center justify-center text-emerald-700 font-bold">R2</div>
            <div className="flex-1">
              <div className="flex justify-between text-xs text-slate-500 mb-1">
                <span>分电压: {isClosed ? v2.toFixed(2) : 0}V</span>
                <span>分电流: {isClosed ? i2.toFixed(2) : 0}A</span>
              </div>
              <div className="w-full bg-slate-100 h-1.5 rounded-full overflow-hidden">
                <div className="bg-emerald-400 h-full" style={{ width: `${isClosed ? (v2/voltage)*100 : 0}%` }}></div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TheoryPanel;
